import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useAuth } from '../../context/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';

const CustomerHeader = ({ cartItemCount: cartItemCountProp = 0, onCartClick }) => {
  const cart = useCart();
  const wishlist = useWishlist();
  // Live count beats whatever prop a page passes in. Pages don't need to
  // wire useCart() themselves to keep the badge accurate. Ported from the
  // bounderlaster pattern after the lalayfashion version had inverted
  // precedence and silently froze the badge at the prop's initial value.
  const count = cart?.totalCount ?? cartItemCountProp;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigationItems = [
    { label: 'Shop', path: '/product-catalog', icon: 'Store' },
    { label: isAuthenticated() && user?.firstName ? user.firstName : 'Account', path: isAuthenticated() ? '/user-account-dashboard' : '/user-login', icon: 'User' },
    { label: 'Wishlist', path: '/wishlist', icon: 'Heart' },
    { label: 'Cart', path: '/shopping-cart', icon: 'ShoppingCart' }
  ];

  const isActivePath = (path) => {
    if (path === '/product-catalog') {
      return location?.pathname === '/product-catalog' || location?.pathname === '/product-details';
    }
    if (path === '/user-account-dashboard') {
      return location?.pathname === '/user-account-dashboard';
    }
    return location?.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleCartClick = () => {
    if (onCartClick) {
      onCartClick();
    }
  };

  const handleAuthClick = () => {
    navigate('/user-login');
  };

  const handleAccountClick = () => {
    if (isAuthenticated()) {
      setShowUserMenu(false);
      setIsMobileMenuOpen(false);
      navigate('/user-account-dashboard');
    } else {
      handleAuthClick();
    }
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);
    logout();
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border shadow-elevation-1">
      <div className="w-full px-4 sm:px-6 lg:px-[15%]">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/landing-page" className="flex items-center space-x-2 flex-shrink-0 hover:opacity-80 transition-opacity">
            <img src="/logo.svg?v=2" alt="Lalay Fashion" className="h-10 w-auto" />
            <span className="font-heading font-semibold text-lg sm:text-2xl text-foreground whitespace-nowrap -ml-1 sm:-ml-2" data-ocwp-field="brandName">
              Lalay Fashion
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            {navigationItems?.map((item) => {
              const handleClick = () => {
                if (item?.label === 'Account') {
                  handleAccountClick();
                } else if (item?.label === 'Cart') {
                  handleCartClick();
                }
              };

              return (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={item?.label === 'Account' || item?.label === 'Cart' ? handleClick : undefined}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                    isActivePath(item?.path)
                      ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                  {item?.label === 'Cart' && count > 0 && (
                    <span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                      {count}
                    </span>
                  )}
                  {item?.label === 'Wishlist' && wishlist?.totalCount > 0 && (
                    <span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                      {wishlist.totalCount}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* User Menu for Desktop */}
            {isAuthenticated() && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
                >
                  <Icon name="ChevronDown" size={16} />
                </button>

                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-background border border-border rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-border">
                        <p className="text-sm font-medium text-foreground">{user?.name || user?.email}</p>
                        <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                      </div>
                      <button
                        onClick={handleAccountClick}
                        className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-accent transition-smooth"
                      >
                        My Account
                      </button>
                      <Link
                        to="/user-account-dashboard?tab=orders"
                        className="block px-4 py-2 text-sm text-foreground hover:bg-accent transition-smooth"
                        onClick={() => setShowUserMenu(false)}
                      >
                        Order History
                      </Link>
                      <div className="border-t border-border my-1"></div>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-destructive hover:bg-accent transition-smooth"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-1">
            {/* Cart Icon for Mobile */}
            <button
              onClick={handleCartClick}
              className="relative p-2 text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="ShoppingCart" size={20} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                  {count}
                </span>
              )}
            </button>

            {/* Account Icon for Mobile */}
            <button
              onClick={handleAccountClick}
              className="p-2 text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="User" size={20} />
            </button>

            {/* Hamburger Menu */}
            <button
              onClick={toggleMobileMenu}
              className="p-2 text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background">
            <nav className="px-2 pt-2 pb-3 space-y-1">
              {navigationItems?.map((item) => {
                const handleMobileClick = () => {
                  setIsMobileMenuOpen(false);
                  if (item?.label === 'Account') {
                    handleAccountClick();
                  } else if (item?.label === 'Cart') {
                    handleCartClick();
                  }
                };

                return (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={handleMobileClick}
                    className={`flex items-center justify-between px-3 py-3 rounded-lg text-base font-medium transition-smooth ${
                      isActivePath(item?.path)
                        ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name={item?.icon} size={20} />
                      <span>{item?.label}</span>
                    </div>
                    {item?.label === 'Cart' && count > 0 && (
                      <span className="bg-primary text-primary-foreground text-sm rounded-full px-2 py-1 min-w-[24px] text-center">
                        {count}
                      </span>
                    )}
                  </Link>
                );
              })}

              {/* Mobile User Menu */}
              {isAuthenticated() && (
                <div className="border-t border-border pt-4 mt-4">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-foreground">{user?.name || user?.email}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                  <Link
                    to="/user-account-dashboard?tab=orders"
                    className="flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon name="Package" size={20} />
                    <span>Order History</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium text-destructive hover:bg-muted transition-smooth w-full"
                  >
                    <Icon name="LogOut" size={20} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default CustomerHeader;